import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listKioskCalendars,
  listKioskGroups,
} from "../../../services/kioskDashboard.service";
import { buildAdministrationQuery } from "./kioskDashboard.helpers";

const PAGE_SIZE = 100;
const EMPTY_DATA = { groups: [], calendars: [] };

const loadAllGroups = async (query) => {
  const firstPage = await listKioskGroups({ ...query, page: 1, pageSize: PAGE_SIZE });
  const groups = [...(firstPage && Array.isArray(firstPage.items) ? firstPage.items : [])];
  const total = Number(firstPage && firstPage.total) || groups.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  for (let page = 2; page <= pageCount; page += 1) {
    const nextPage = await listKioskGroups({ ...query, page, pageSize: PAGE_SIZE });
    groups.push(...(nextPage && Array.isArray(nextPage.items) ? nextPage.items : []));
  }
  return groups;
};

export const useKioskAdministrationData = (filters = {}, enabled = true) => {
  const [state, setState] = useState({ data: EMPTY_DATA, loading: true, error: null });
  const mountedRef = useRef(false);
  const requestRef = useRef(0);
  const filterKey = JSON.stringify(filters || {});
  const stableFilters = useMemo(() => JSON.parse(filterKey), [filterKey]);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (mountedRef.current) setState(previous => ({ ...previous, loading: true, error: null }));
    try {
      const query = buildAdministrationQuery(stableFilters);
      const calendarQuery = {};
      if (query.fechaDesde) calendarQuery.cicloDesde = query.fechaDesde;
      if (query.fechaHasta) calendarQuery.cicloHasta = query.fechaHasta;

      const [groups, calendarsPage] = await Promise.all([
        loadAllGroups(query),
        listKioskCalendars({ ...calendarQuery, page: 1, pageSize: PAGE_SIZE }),
      ]);

      if (mountedRef.current && requestRef.current === requestId) {
        setState({
          data: {
            groups,
            calendars: calendarsPage && Array.isArray(calendarsPage.items)
              ? calendarsPage.items
              : [],
          },
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      if (mountedRef.current && requestRef.current === requestId) {
        setState({ data: EMPTY_DATA, loading: false, error });
      }
    }
  }, [stableFilters]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      load();
    } else {
      setState(previous => ({ ...previous, loading: false, error: null }));
    }
    return () => {
      mountedRef.current = false;
    };
  }, [load, enabled]);

  return {
    ...state,
    reload: load,
  };
};
